import React, {useMemo} from 'react';
import {Box, Flex, VStack} from '@chakra-ui/react';
import {motion} from 'framer-motion';
import {TextA16, TextA18, TextA20} from '../typography';
import styled from 'styled-components';
import {useNavigate, useLocation, useSearchParams} from 'react-router-dom';
import Icon from '../Icon';
import {BlueButton} from '../button';
import useAuth from '../../hooks/useAuth';
import {Offer} from '../../types/article';

interface MemberOffersValueCardProps {
  offers: Offer[];
}

const ValueCard = styled(motion.div)`
  display: block;
  width: 100%;
  min-height: 100px;
  border-radius: 16px;
  padding: 20px 24px;
  position: relative;
  overflow: visible;
  cursor: pointer;
  background: linear-gradient(135deg, rgba(66, 153, 225, 0.95) 0%, rgba(102, 126, 234, 0.95) 100%);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(66, 153, 225, 0.4), 0 0 40px rgba(66, 153, 225, 0.2);
  isolation: isolate;
  z-index: 1;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 375px) {
    padding: 18px 20px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1);
    opacity: 0.6;
    pointer-events: none;
    z-index: 0;
  }

  &:hover::before {
    opacity: 0.8;
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.4), inset 0 0 25px rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const GlowEffect = styled.div`
  position: absolute;
  top: -30px;
  right: -30px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
  z-index: 0;
  opacity: 0.5;
  filter: blur(20px);
`;

const ValueBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 24px;
  font-weight: 700;
  font-size: 20px;
  background: rgba(255, 255, 255, 0.95);
  color: #2c5aa0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 375px) {
    font-size: 18px;
    padding: 6px 14px;
  }
`;

const ActionButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 12px;
  color: #2c5aa0;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const GetMembershipCard = styled(motion.div)`
  display: block;
  width: 100%;
  min-height: 120px;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: visible;
  cursor: pointer;
  background: linear-gradient(135deg, rgba(237, 100, 166, 0.95) 0%, rgba(245, 115, 127, 0.95) 100%);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(237, 100, 166, 0.4), 0 0 40px rgba(237, 100, 166, 0.2);
  isolation: isolate;
  z-index: 1;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 375px) {
    padding: 20px;
    min-height: 110px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1);
    opacity: 0.6;
    pointer-events: none;
    z-index: 0;
  }

  &:hover::before {
    opacity: 0.8;
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.4), inset 0 0 25px rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MemberOffersValueCard: React.FC<MemberOffersValueCardProps> = ({offers}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {user} = useAuth();

  const isProduction = process.env.NODE_ENV === 'production';
  const insiderClubInterestId = isProduction ? '294' : '212';

  // Filter for member-only offers (is_private = true)
  const memberOnlyOffers = useMemo(() => {
    return offers.filter((offer) => offer.is_private && offer.communities?.length > 0);
  }, [offers]);

  const totalRetailValue = useMemo(() => {
    return memberOnlyOffers.reduce((total, offer) => {
      return total + (offer.retail_price ? parseFloat(offer.retail_price.toString()) : 0);
    }, 0);
  }, [memberOnlyOffers]);

  const offerCount = memberOnlyOffers.length;

  // Don't show card if no member-only offers with retail value
  if (offerCount === 0 || totalRetailValue === 0) {
    return null;
  }

  const handleNavigate = () => {
    if (!user?.hasActiveMembership) {
      navigate('/memberships');
    } else {
      // Check if we're already on the offer page
      const isOnOfferPage = location.pathname.includes('/search/offer');
      const currentTopics = searchParams.get('topics');
      
      // If already on offer page with the topic, clear other filters and keep the topic
      if (isOnOfferPage) {
        // Force a refresh by navigating with replace: false
        const newParams = new URLSearchParams();
        newParams.set('topics', insiderClubInterestId.toString());
        navigate(`/search/offer?${newParams.toString()}`, {replace: false});
      } else {
        navigate(`/search/offer?topics=${insiderClubInterestId}`);
      }
    }
  };

  // Non-member version - encourage sign up
  if (!user?.hasActiveMembership) {
    return (
      <Box
        mb={4}
        mt={2}
        position="relative"
        overflow="visible"
        width="100%"
        minHeight="150px"
        bg="transparent">
        <GlowEffect />
        <GetMembershipCard
          onClick={handleNavigate}
          whileHover={{y: -2}}
          whileTap={{scale: 0.98}}
          transition={{duration: 0.2}}
          css={`
            &:focus,
            &:focus-visible {
              outline: none;
              box-shadow: none;
            }
          `}>
          <VStack spacing={3} align="stretch" position="relative" zIndex={2}>
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
              <TextA20 color="white" fontWeight="800" textShadow="0 2px 4px rgba(0,0,0,0.2)">
                Insider Exclusive Perks
              </TextA20>
              <ValueBadge>${Math.floor(totalRetailValue)} Value</ValueBadge>
            </Flex>
            <TextA16 color="white" fontWeight="600" opacity={0.95}>
              Insiders get access to {offerCount} exclusive {offerCount === 1 ? 'perk' : 'perks'}{' '}
              worth ${Math.floor(totalRetailValue)}!
            </TextA16>
            <ActionButton
              as={motion.button}
              whileHover={{scale: 1.02}}
              transition={{duration: 0.2}}
              css={`
                &:focus,
                &:focus-visible {
                  outline: none;
                  box-shadow: none;
                }
              `}>
              <TextA18 color="#2c5aa0" fontWeight="700">
                Become an Insider
              </TextA18>
              <Icon
                iconName="fi fi-rr-arrow-small-right"
                style={{
                  color: '#2c5aa0',
                  fontSize: '24px',
                  marginLeft: '4px',
                }}
              />
            </ActionButton>
          </VStack>
        </GetMembershipCard>
      </Box>
    );
  }

  // Member version - show value and link to offers
  return (
    <Box
      mb={4}
      mt={2}
      position="relative"
      overflow="visible"
      width="100%"
      minHeight="150px"
      bg="transparent">
      <GlowEffect />
      <ValueCard
        onClick={handleNavigate}
        whileHover={{y: -2}}
        whileTap={{scale: 0.98}}
        transition={{duration: 0.2}}
        css={`
          &:focus,
          &:focus-visible {
            outline: none;
            box-shadow: none;
          }
        `}>
        <VStack spacing={3} align="stretch" position="relative" zIndex={2}>
          <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
            <TextA20 color="white" fontWeight="800" textShadow="0 2px 4px rgba(0,0,0,0.2)">
              Your Insider Perks
            </TextA20>
            <ValueBadge>${Math.floor(totalRetailValue)} Value</ValueBadge>
          </Flex>
          <TextA16 color="white" fontWeight="600" opacity={0.95}>
            You have {offerCount} exclusive {offerCount === 1 ? 'perk' : 'perks'} available!
          </TextA16>
          <ActionButton
            as={motion.button}
            whileHover={{scale: 1.02}}
            transition={{duration: 0.2}}
            css={`
              &:focus,
              &:focus-visible {
                outline: none;
                box-shadow: none;
              }
            `}>
            <TextA18 color="#2c5aa0" fontWeight="700">
              View All Perks
            </TextA18>
            <Icon
              iconName="fi fi-rr-arrow-small-right"
              style={{
                color: '#2c5aa0',
                fontSize: '24px',
                marginLeft: '4px',
              }}
            />
          </ActionButton>
        </VStack>
      </ValueCard>
    </Box>
  );
};

export default MemberOffersValueCard;
